import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clustersAPI } from '../services/api';
import { useAuth } from './AuthContext';

const ClusterContext = createContext(null);

export function ClusterProvider({ children }) {
  const [clusters, setClusters]             = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);  // full cluster object
  const [resources, setResources]           = useState(null);    // live k8s resources
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState(null);

  const { user } = useAuth();

  // Load clusters when user logs in, clear when logs out
  useEffect(() => {
    if (user) {
      loadClusters();
    } else {
      setClusters([]);
      setSelectedCluster(null);
      setResources(null);
    }
  }, [user]);

  const loadClusters = async () => {
    try {
      const res = await clustersAPI.list();
      const list = res.data;
      setClusters(list);

      // Restore previously selected cluster from localStorage
      const savedId = localStorage.getItem('selectedClusterId');
      if (savedId) {
        const found = list.find(c => String(c.id) === savedId);
        if (found) {
          setSelectedCluster(found);
          return;
        }
      }
      // Default: first active cluster
      const active = list.find(c => c.status === 'active');
      if (active) setSelectedCluster(active);
    } catch (e) {
      console.error('Failed to load clusters', e);
    }
  };

  // Fetch live resources whenever selected cluster changes
  const loadResources = useCallback(async (cluster) => {
    if (!cluster || !cluster.is_imported) {
      setResources(null);
      return;
    }
    setResourcesLoading(true);
    setResourcesError(null);
    try {
      const res = await clustersAPI.getResources(cluster.id);
      setResources(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // An agent-based cluster with no agent connected returns detail as an OBJECT
      // ({message, action_required, network_type} — clusters/resources.py), not a string.
      // resourcesError is read directly as a JSX child by every consumer (Pods.js,
      // Namespaces.js) — rendering the raw object crashed the whole page with React
      // error #31 (confirmed live on kube.sarv.com/pods, cluster jpr-dc-beta-13). Same
      // shape ClusterResources.js already unwraps correctly for its OWN getResources call;
      // this is the shared context's call, which every consumer of resourcesError relies on.
      setResourcesError(
        (typeof detail === 'object' ? detail?.message : detail) || 'Failed to fetch cluster resources'
      );
      setResources(null);
    } finally {
      setResourcesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCluster) {
      localStorage.setItem('selectedClusterId', String(selectedCluster.id));
      loadResources(selectedCluster);
    }
  }, [selectedCluster, loadResources]);

  const selectCluster = (cluster) => {
    setSelectedCluster(cluster);
    setResources(null);
  };

  const refreshResources = () => loadResources(selectedCluster);

  return (
    <ClusterContext.Provider value={{
      clusters,
      selectedCluster,
      selectCluster,
      resources,
      resourcesLoading,
      resourcesError,
      refreshResources,
      reloadClusters: loadClusters,
    }}>
      {children}
    </ClusterContext.Provider>
  );
}

export function useCluster() {
  const ctx = useContext(ClusterContext);
  if (!ctx) throw new Error('useCluster must be used inside ClusterProvider');
  return ctx;
}
